import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2 } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [mfa, setMfa] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, totp: totp || undefined });
      if (data.mfa_required) { setMfa(true); setBusy(false); return; }
      login(data.token, data.user);
      nav(data.user.platform_role === "admin" ? "/admin" : "/app/dashboard");
    } catch (e) { setErr(apiErr(e)); setBusy(false); }
  };

  return (
    <div className="flex min-h-screen bg-[#06080D]">
      <div className="hidden w-1/2 flex-col justify-between border-r border-slate-800 p-12 lg:flex zk-grid-bg">
        <Logo />
        <div>
          <h2 className="font-display text-3xl font-bold text-slate-50">Your offsite backups, always watching your back.</h2>
          <p className="mt-4 max-w-md text-slate-400">Sign in to monitor backup health, storage, verification and your managed Proxmox Backup Server.</p>
        </div>
        <div className="text-xs text-slate-600">ZettaKeep — Offsite Proxmox backups in minutes.</div>
      </div>
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-display text-2xl font-bold text-slate-50">{mfa ? "Two-factor authentication" : "Welcome back"}</h1>
          <p className="mt-1 text-sm text-slate-400">{mfa ? "Enter the 6-digit code from your authenticator app." : "Sign in to your ZettaKeep account."}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            {!mfa ? (
              <>
                <Input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="bg-slate-950 border-slate-700" />
                <Input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="bg-slate-950 border-slate-700" />
              </>
            ) : (
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={totp} onChange={setTotp} data-testid="login-totp">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            )}
            {err && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="login-error">{err}</div>}
            <button disabled={busy} data-testid="login-submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-cyan-400 py-2.5 font-semibold text-[#06080D] hover:bg-cyan-300 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {mfa ? "Verify" : "Sign in"}
            </button>
          </form>
          <p className="mt-6 text-sm text-slate-500">No account? <Link to="/signup" className="text-cyan-300 hover:underline">Start free</Link></p>
        </div>
      </div>
    </div>
  );
}
